import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchWorkdayStatus } from './api';
import { isWorkdayGatedRole, WORKDAY_STEPS } from './constants';

const WorkdayGateContext = createContext(null);
const GATE_CACHE_PREFIX = 'tauris-workday-gate:';

function cacheKeyForUser(user) {
    const id = user?.id || user?._id;
    return id ? `${GATE_CACHE_PREFIX}${id}` : null;
}

function readCachedGate(user) {
    const key = cacheKeyForUser(user);
    if (!key) return null;
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.gate) return null;
        return parsed.gate;
    } catch {
        return null;
    }
}

function writeCachedGate(user, gate) {
    const key = cacheKeyForUser(user);
    if (!key || !gate) return;
    try {
        localStorage.setItem(key, JSON.stringify({ gate, at: Date.now() }));
    } catch {
        /* quota / private mode */
    }
}

function offlineFallbackGate(user) {
    const cached = readCachedGate(user);
    if (cached) return { ...cached, offline: true };
    // Allow app shell when we have no cache — better than infinite spinner
    return {
        gated: true,
        blocked: false,
        step: WORKDAY_STEPS.CLEAR,
        offline: true,
    };
}

export function WorkdayGateProvider({ children }) {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const [gate, setGate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const gated = Boolean(user && isWorkdayGatedRole(user.role));

    const refresh = useCallback(async ({ silent = false } = {}) => {
        if (!isAuthenticated || !user || !isWorkdayGatedRole(user.role)) {
            setGate({ gated: false, blocked: false, step: WORKDAY_STEPS.CLEAR });
            return null;
        }

        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            const offlineGate = offlineFallbackGate(user);
            setGate(offlineGate);
            setError('');
            if (!silent) setLoading(false);
            return offlineGate;
        }

        if (!silent) setLoading(true);
        try {
            const data = await fetchWorkdayStatus();
            setGate(data);
            writeCachedGate(user, data);
            setError('');
            return data;
        } catch (e) {
            const offlineish = e.message === 'Network Error'
                || e.code === 'ERR_NETWORK'
                || (typeof navigator !== 'undefined' && !navigator.onLine)
                || !e.response;

            if (offlineish) {
                const offlineGate = offlineFallbackGate(user);
                setGate(offlineGate);
                setError('');
                return offlineGate;
            }

            // Keep last gate if we have one — avoid blank spinner
            const cached = readCachedGate(user);
            if (cached) {
                setGate(cached);
                setError(e.response?.data?.error || 'Using last known workday status');
                return cached;
            }

            setError(e.response?.data?.error || 'Failed to validate workday status');
            return null;
        } finally {
            if (!silent) setLoading(false);
        }
    }, [isAuthenticated, user]);

    useEffect(() => {
        if (authLoading) return;
        if (!isAuthenticated || !user) {
            setGate(null);
            return;
        }
        // Seed from cache immediately so ProtectedRoute does not hang offline
        if (isWorkdayGatedRole(user.role)) {
            const cached = readCachedGate(user);
            if (cached) setGate(cached);
        }
        refresh();
    }, [authLoading, isAuthenticated, user, refresh]);

    const value = useMemo(() => ({
        gate,
        loading: authLoading || loading,
        error,
        gated,
        blocked: Boolean(gate?.blocked),
        step: gate?.step || WORKDAY_STEPS.CLEAR,
        refresh,
        setGate,
    }), [gate, authLoading, loading, error, gated, refresh]);

    return (
        <WorkdayGateContext.Provider value={value}>
            {children}
        </WorkdayGateContext.Provider>
    );
}

export function useWorkdayGate() {
    const ctx = useContext(WorkdayGateContext);
    if (!ctx) {
        throw new Error('useWorkdayGate must be used within WorkdayGateProvider');
    }
    return ctx;
}
